import { Accessor, Component, JSX, ParentProps } from 'solid-js';

import { createListTransition, noop } from './listTransition';
import { animate, AnimateOptions, AnimateReturn } from './animate';

function onExit(transitions: TransitionInfo[], onFinish: () => any) {
  return Promise.all(
    transitions.map((transition) => {
      return new Promise<void>((resolve) => transition.out(() => resolve()));
    })
  ).then(onFinish);
}

export function transition(el: HTMLElement, options: Options) {
  const { in: isIntro = false, out: isOutro = false } = options;
  // const active = useIsActive();

  const pending = usePending();
  if (!pending) return;
  const [inTransitions, outTransitions] = pending;
  // let isInited = options.appear !== false ? Inited.has(pending) : true;
  // let active = options.active !== false ? useIsActive() : () => true;
  let intro: AnimateReturn | undefined, outro: AnimateReturn | undefined;
  let currentOptions: AnimateOptions | undefined;
  async function getKeyFrames(type: TransitionType): Promise<AnimateOptions> {
    if (!currentOptions) {
      currentOptions = await untrack(() => options.config(el, type));
    }
    return currentOptions;
  }

  // const owner = getOwner();
  const transition: TransitionInfo = {
    el,
    enable: true,
    async in() {
      // console.log('in', el)
      const options = await getKeyFrames('in');

      intro = animate(el, options, outro, 1, () => {
        intro = currentOptions = undefined;
      });
    },
    async out(fn: Function) {
      const options = await getKeyFrames('out');
      outro = animate(el, options, intro, 0, () => {
        outro = currentOptions = undefined;
        fn();
      });
      OutAne.set(el, outro);
      // 清理
      if (!transition.enable) {
        outTransitions.delete(transition);
        transition.out = noop;
      }
    },
  };
  if (!isOutro) {
    inTransitions.add(transition);
    onCleanup(() => {
      inTransitions.delete(transition);
    });
  }

  if (!isIntro) {
    outTransitions.add(transition);
    onCleanup(() => {
      transition.enable = false;
    });
  }
}

const OutAne = new WeakMap<Element, AnimateReturn>();

const PendingContext = createContext<[Set<TransitionInfo>, Set<TransitionInfo>]>();
function usePending() {
  return useContext(PendingContext);
}

export function Presence(
  props: ParentProps<{
    appear?: boolean;
  }>
) {
  const inTransitions = new Set<TransitionInfo>();
  const outTransitions = new Set<TransitionInfo>();
  // const active = useIsActive();
  // createEffect(() => {
  //   if (active()) {
  //     Inited.add(pending);
  //     onCleanup(() => Inited.delete(pending));
  //   }
  // });

  const childs = children(() => (
    <PendingContext.Provider value={[inTransitions, outTransitions]}>{props.children}</PendingContext.Provider>
  )) as unknown as Accessor<Element[]>;

  const res = createListTransition(childs, {
    onChange({ added, removed, finishRemoved }) {
      if (added.length > 0) {
        queueMicrotask(() => {
          for (const el of added) {
            const transitions = Array.from(inTransitions).filter((it) => el.contains(it.el));
            for (const transition of transitions) {
              transition.in(noop);
            }
          }
        });
      }

      batch(() => {
        for (const el of removed) {
          // const transitions = TransitionCache.get(el)?.[1];
          if (!(el instanceof Element)) {
            finishRemoved([el]);
          } else {
            const transitions = Array.from(outTransitions).filter((it) => el.contains(it.el));
            // console.log('remove', el)
            const noTransition = transitions.length === 0;
            if (noTransition) {
              finishRemoved([el]);
            } else {
              onExit(transitions, () => {
                finishRemoved([el]);
              });
            }
          }
        }
      });
    },
  }) as any;

  return res as unknown as JSX.Element;
}

export function presenceify<T extends Component<any>>(Component: T): T {
  return ((props: any) => {
    return (
      <Presence>
        <Component {...props} />
      </Presence>
    );
  }) as T;
}

function children<T = any>(fn: Accessor<T>): Accessor<T[]> {
  const children2 = createMemo(fn);
  return createMemo(
    () => {
      const clds = resolveChildren(children2());
      return Array.isArray(clds) ? clds.filter(Boolean) : clds != null ? [clds] : [];
    },
    void 0
  );
}

function resolveChildren(child: any): any {
  if (typeof child === 'function' && !child.length) return resolveChildren(child());
  if (Array.isArray(child)) {
    const results = [];
    for (let i = 0; i < child.length; i++) {
      const result = resolveChildren(child[i]);
      Array.isArray(result) ? results.push.apply(results, result) : results.push(result);
    }
    return results;
  }
  const isElement = child ? (import.meta.env.SSR ? typeof child.t === 'string' : child instanceof Element) : false;
  if (isElement && !TransitionCache.has(child)) {
    TransitionCache.set(child, [new Set(), new Set()]);
  }
  return child;
}

// const OutTransitions = new WeakMap<Element, Set<TransitionInfo>>();
// const InTransitions = new WeakMap<Element, Set<TransitionInfo>>();
const TransitionCache = new WeakMap<Element, [Set<TransitionInfo>, Set<TransitionInfo>]>();

export interface TransitionRes {
  transitions: Set<TransitionInfo>;
  children(): JSX.Element;
}

export interface TransitionInfo {
  // state: TransitionState;
  el: HTMLElement;
  enable: boolean;
  in(fn: () => void): void;
  out(fn: () => void): void;
}

// const enum TransitionState {
//   INTRO_START,
//   INTRO_END,
//   OUTRO_START,
//   OUTRO_END,
// }

export type TransitionType = 'in' | 'out' | 'both';

export type Keyframes = [Keyframe, Keyframe];

export type Options = {
  in?: boolean;
  out?: boolean;
  both?: boolean;
  /** 初次渲染是否进行动画 */
  appear?: boolean;
  /** 是否响应active状态动画 */
  active?: boolean;
  delay?: number;
  easing?(t: number): number;
  /** 默认350ms */
  duration?: number;
  config(el: HTMLElement, type: TransitionType): Promise<AnimateOptions> | AnimateOptions;
};
